import Vue from "vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import routes, { prefetchCommonPages } from "@/routes/routes";
import { BootstrapVue, BootstrapVueIcons } from "bootstrap-vue";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue/dist/bootstrap-vue.css";
import "@/styles/custom.scss";
import VueRouter, { NavigationGuardNext, RawLocation, Route } from "vue-router";
import App from "./App.vue";
import DashboardPlugin from "./dashboard-plugin";
import Notifications from "@/components/NotificationPlugin";
import { AxiosStatic, default as axios } from "axios";
import moment from "moment";
import vueNumeralFilterInstaller from "vue-numeral-filter";
import * as signalR from "@microsoft/signalr";
import { User } from "./models/User";
import { printElementById } from '@/util/printElement';
import { NotificationOptions as PrintNotificationOptions } from '@/util/NotificationOptionsPresets';
import VueMask from "v-mask";
import Button from "@/components/Button.vue";
import Card from "@/components/Cards/Card.vue";
import { DashboardStatus } from "./models/DashboardStatus";
import { PhoneStats } from "./models/PhoneStats";
import LoadScript from "vue-plugin-load-script";
import MicrosoftAuth from "@/util/microsoftAuth";
import { dashboardSetup } from "./util/dashboardSetup";
import { inactivitySetup } from "./util/inactivity/inactivitySetup";
import { presenceSetup } from "./util/presence/presenceSetup";
import { idleInactivityState } from "./util/inactivity/inactivityStateMachine";
import { parseHostOrigins } from "./util/inactivity/embeddedHost";
import { initUserSettings } from "./services/UserSettingsService";
import { initTenantConfig } from "./services/tenantConfigService";
import { BrowserAuthError } from "@azure/msal-browser";
import { getToken as getCognitoToken } from "@/util/cognitoAuth";
import { Permission } from "@/models/Permission";
import { marked } from "marked";
import { AuthProviderFactory } from "@/util/authProviderFactory";
import { formatPhoneFilter, formatPhoneTwilioFilter } from "@/util/PhoneFormatter";
import { formatDurationFilter } from "@/util/DurationFormatter";
import { AuthProviderType } from "@/util/authProviderInterface";
import userInfoService, { UserInfoError, UserInfoServiceError } from "@/util/userInfoService";
import { requiredPermissionLabel, routeMetaAllows } from "@/util/routeAccess";
import { initUpdateCheck, installUpdateNavigationGuard } from "@/util/updateCheck";
import { trackNavigationWait } from "@/util/navigationProgress";
import { installRoutePrefetchGuard } from "@/util/routePrefetch";
import { hideSplash } from "@/util/splash";
import 'element-ui/lib/theme-chalk/index.css';

// Short enough that the Unauthorized page's immediate retry reuses the recheck it was sent from.
const DENIAL_RECHECK_AGE_MS = 5_000;

// Extend Vue prototype
declare module 'vue/types/vue' {
    interface Vue {
        $authProviderType: AuthProviderType;
    }
}

// Configuration interface
interface AppConfig {
    VITE_APP_USE_COGNITO: string;
    VITE_APP_AUTH_PROVIDER?: string;
    VITE_APP_ENVIRONMENT_NAME: string;
    VITE_STRIPE_PUBLIC_KEY: string;
    VITE_APP_APIURL: string;
    VITE_APP_ADAL_GRAPHID: string;
    VITE_APP_ADAL_TENANT: string;
    VITE_APP_CENTRAL_FILL_ENABLED: string;
    /** "true" (or a JSON true) rolls out the welcome modal and guided tour; see services/onboardingRollout. */
    VITE_APP_ONBOARDING_ENABLED?: string | boolean;
    VITE_APP_OIDC_AUTHORITY?: string;
    VITE_APP_OIDC_CLIENT_ID?: string;
    VITE_APP_OIDC_SCOPES?: string;
    VITE_APP_OIDC_KNOWN_AUTHORITIES?: string;
    VITE_APP_OIDC_PROMPT?: string;
    /** Comma-separated origins trusted to embed the app and report host activity; defaults to Twilio Flex. */
    VITE_APP_EMBED_HOST_ORIGINS?: string;
}

// Load configuration asynchronously
async function loadConfig(): Promise<AppConfig> {
    try {
        const response = await fetch('/appConfig.json');
        if (!response.ok) {
            throw new Error(`Failed to load config: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Failed to load appConfig.json:', error);
        // Fallback to empty config or defaults
        return {
            VITE_APP_USE_COGNITO: 'false',
            VITE_APP_ENVIRONMENT_NAME: '',
            VITE_STRIPE_PUBLIC_KEY: '',
            VITE_APP_APIURL: '',
            VITE_APP_ADAL_GRAPHID: '',
            VITE_APP_ADAL_TENANT: '',
            VITE_APP_CENTRAL_FILL_ENABLED: 'false'
        };
    }
}

// Declaring global variables
declare module "vue/types/vue" {
    interface Vue {
        $http: AxiosStatic;
        $user: User;
        $stripeKey: string;
        $permissions: Permission[];
        $remoteDisabledStates: string[];
        $stateRestrictedStates: string[];
        $canNavigateTo: (location: RawLocation | null | undefined) => boolean;
        $pageNames: Record<string, string>;
        $config: Record<string, string>;
        $environment: string;
        $marked: typeof marked;
        // Installed in dashboardSetup; absent when the browser has no SharedWorker support.
        $workerPort?: MessagePort;
    }
}
// Initialize the application
async function initializeApp() {
    const config = await loadConfig();

    // Add config to Vue prototype
    Vue.prototype.$config = config;

    const useCognito = config.VITE_APP_USE_COGNITO === "true";

    Vue.prototype.$environment = config.VITE_APP_ENVIRONMENT_NAME ?? "";
    Vue.prototype.$version = "";

    const serverURL = config.VITE_APP_APIURL ?? '';

    axios.defaults.baseURL = `${serverURL}/api`;
    axios.defaults.headers.post["Content-Type"] = "application/json";
    axios.defaults.withCredentials = true;
    const router = new VueRouter({
        routes,
        mode: "history",
        linkActiveClass: "active",
        scrollBehavior(to: any, _: any, savedPosition: any) {
            if (to?.meta?.preserveScroll || to?.query?.preserveScroll) return {};
            if (savedPosition) return savedPosition;
            return { x: 0, y: 0 };
        }
    } as Record<string, any>);

    // A route's own meta is the single source of truth for who may reach it. Both the
    // navigation guard below and $canNavigateTo answer from here, so a link can never
    // offer a destination the guard would bounce to /unauthorized.
    const metaAllows = (meta: any): boolean => routeMetaAllows(meta, Vue.prototype.$permissions);

    const canNavigateTo = (location: RawLocation | null | undefined): boolean => {
        if (!location) return false;

        const { route } = router.resolve(location);
        // An unresolvable name falls back to the current route rather than failing, which
        // would answer using whatever page the user is already on. Refuse instead.
        if (!route.matched.length) return false;

        return metaAllows(route.meta);
    };

    Vue.prototype.$canNavigateTo = canNavigateTo;

    // Registered before the auth guard so a pending update hard-navigates without first
    // paying for the user-info fetch the auth guard performs.
    installUpdateNavigationGuard(router);

    // Ahead of the auth guard, so a page's chunk downloads while that guard waits on user info.
    installRoutePrefetchGuard(router);

    const authGuard = async (to: Route, next: NavigationGuardNext): Promise<void> => {
        // requiresAuth covers routes open to every signed-in user. Without it, a
        // route carrying no permission would skip this block entirely and render
        // to an anonymous visitor -- metaAllows() below still returns true for
        // them, so only the sign-in step is being added, not a permission check.
        if (to.meta?.permission || to.meta?.anyPermission || to.meta?.requiresAnyPermission || to.meta?.requiresAuth) {
            const intendedPath = encodeURIComponent(to.fullPath);

            try {
                if (!useCognito) {
                    await MicrosoftAuth.ensureAuthenticated();

                    if (!MicrosoftAuth.isTokenValid()) {
                        await MicrosoftAuth.fetchToken();
                    }
                }

                // Waits only until the first load; after that the cached permissions answer,
                // and PermissionsChanged pushes keep them current.
                await userInfoService.getUserInfo();
            } catch (error) {
                if (error instanceof UserInfoServiceError) {
                    switch (error.type) {
                        case UserInfoError.AUTH_ERROR:
                            next(`/unauthorized?authError=1&redirect=${intendedPath}`);
                            break;
                        case UserInfoError.NETWORK_ERROR:
                            next(`/unauthorized?networkError=1&redirect=${intendedPath}`);
                            break;
                        default:
                            console.error("Route guard error:", error);
                            next(`/unauthorized?networkError=1&redirect=${intendedPath}`);
                            break;
                    }
                } else {
                    next(`/unauthorized?networkError=1&redirect=${intendedPath}`);
                }
                return;
            }

            // The cache may allow, but a denial is checked against a fresh copy first, so a
            // permission granted while its push was missed doesn't block the page.
            if (!metaAllows(to.meta)) {
                await userInfoService.refreshIfOlderThan(DENIAL_RECHECK_AGE_MS);
            }

            if (!metaAllows(to.meta)) {
                // requiresAnyPermission has no one code to name, so the page
                // is left to say only that access was refused.
                // Encoded like redirect below: codes carry spaces and slashes,
                // and anyPermission joins several with " or ".
                const required = requiredPermissionLabel(to.meta);
                const permissionQuery = required ? `permission=${encodeURIComponent(required)}&` : "";
                next(`/unauthorized?${permissionQuery}redirect=${intendedPath}`);
                return;
            }
        }

        if (to.path.startsWith("/cphub")) {
            next(to.path.replace("/cphub", "/processing"));
        } else {
            next();
        }
    };

    // Tracked as one wait so the progress bar covers every await in the guard, the MSAL token fetch included.
    router.beforeEach((to, _, next) => {
        void trackNavigationWait(authGuard(to, next));
    });

    // Initialize authentication using factory pattern
    const authProviderType = AuthProviderFactory.getAuthProviderType(config);
    const authProvider = AuthProviderFactory.createProvider(authProviderType);

    // Make authProviderType available globally
    Vue.prototype.$authProviderType = authProviderType;

    if (authProviderType === "Cognito") {
        authProvider.install(Vue, { serverUrl: serverURL });
    } else {
        // MSAL or OIDC
        const msalConfig = AuthProviderFactory.createMsalConfig(authProviderType, config);
        authProvider.install(Vue, {
            serverUrl: serverURL,
            msalConfig: msalConfig,
            authProviderType: authProviderType
        });

        // A returning loginRedirect's account exists only once MSAL has read the response out of the
        // URL. Doing that before the first navigation lets the root route's guard see it, rather than
        // render the sign-in page for a user who is already signed in. The splash covers the wait.
        try {
            await Vue.prototype.$msal.instance.handleRedirectPromise();
        } catch (error) {
            // The response always returns to "/", where the sign-in page reads this to say why the
            // user is back there instead of signed in.
            console.error("Processing the sign-in response failed:", error);
            Vue.prototype.$msalSignInFailed = true;
        }
    }

    Vue.config.productionTip = true;
    Vue.prototype.$http = axios;

    Vue.use(BootstrapVue, { BTable: { sortIconLeft: true, noSortReset: true } });
    Vue.use(BootstrapVueIcons);
    Vue.use(VueRouter);
    Vue.use(DashboardPlugin);
    Vue.use(Notifications);
    Vue.use(vueNumeralFilterInstaller, { locale: "en" });
    Vue.use(VueMask);

    // @ts-ignore
    Vue.use(LoadScript);

    // Replaces vue-html-to-paper, which never called document.close() on the window it wrote and so
    // left it loading forever with the print dialog never opening. See util/printElement.
    Vue.prototype.$htmlToPaper = (elementId: string, callback?: () => void) =>
        printElementById(elementId, callback).catch((error: unknown) => {
            console.error("Error while printing", { elementId, error });
            Vue.prototype.$notification(PrintNotificationOptions.error(error));
        });

    // Preserve the prior vue-showdown behavior: gfm auto-links bare URLs
    // (simplifiedAutoLink), and the link renderer opens links in a new tab
    // (openLinksInNewWindow) so navigation does not leave the SPA.
    marked.setOptions({ gfm: true });
    marked.use({
        renderer: {
            link(href, title, text) {
                const titleAttr = title ? ` title="${title}"` : "";
                return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
            },
        },
    });
    Vue.prototype.$marked = marked;

    Vue.component("VSelect", vSelect);

    Vue.component(Button.name, Button);
    Vue.component(Card.name, Card);

    const store = Vue.observable({
        queueItems: [],
        autoModeEnabled: false,
        currentAutoModeQueue: null,
        autoModeSessionId: null,
        lastAutoModeResponse: null,
        // Ingestion queue item ids the user has routed during the current auto-mode session; the
        // next-item fetch skips these so a just-routed (still-incomplete) item is not handed back.
        autoModeRoutedItemIds: [],
        // Kept separate from autoModeRoutedItemIds: online order ids are an independent id space.
        autoModeRoutedOnlineOrderIds: []
    });
    Vue.prototype.$store = store;

    // Custom filters, this should be better to be declared in a separate folder if we use more of this custom filters
    Vue.filter("formatDate", (value: Date, format: string, setLocal = true) => {
        if (value && format) {
            let momentDate = moment.utc(value);
            momentDate = setLocal ? momentDate.local() : momentDate;
            return momentDate.format(format);
        } else return "";
    });

    Vue.filter("formatLocalDate", (value: Date, format: string, _setLocal = true) => {
        if (value && format) {
            const momentDate = moment(value);
            return momentDate.format(format);
        } else return "";
    });

    // Phone number formatting filters
    Vue.filter("formatPhone", formatPhoneFilter);
    Vue.filter("formatPhoneTwilio", formatPhoneTwilioFilter);

    // Call/recording duration formatting filter (seconds → m:ss)
    Vue.filter("formatDuration", formatDurationFilter);

    // SD-3288: Auto-format slashless date input (MMDDYYYY → MM/DD/YYYY) for all el-date-picker components.
    // Uses capture-phase listeners so formatting happens before Element UI's handlers parse the value.
    function formatSlashlessDate(target: HTMLInputElement): void {
        if (target.tagName !== 'INPUT') return;
        if (!target.closest('.el-date-editor')) return;

        const val = target.value.trim();
        if (!/^\d{8}$/.test(val)) return;

        const month = parseInt(val.substring(0, 2));
        const day = parseInt(val.substring(2, 4));
        const year = parseInt(val.substring(4, 8));

        if (month < 1 || month > 12 || day < 1 || day > 31) return;
        if (year < 1900 || year > 2099) return;

        const date = new Date(year, month - 1, day);
        if (date.getMonth() !== month - 1 || date.getDate() !== day) return;

        const formatted = `${val.substring(0, 2)}/${val.substring(2, 4)}/${val.substring(4, 8)}`;
        target.value = formatted;
        target.dispatchEvent(new Event('input', { bubbles: true }));
        target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    document.addEventListener('blur', (event: FocusEvent) => {
        if (event.target) formatSlashlessDate(event.target as HTMLInputElement);
    }, true);

    document.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter' && event.target) {
            formatSlashlessDate(event.target as HTMLInputElement);
        }
    }, true);

    const updateTime: Date | null = null;

    const inactivityOptions = { hostOrigins: parseHostOrigins(config.VITE_APP_EMBED_HOST_ORIGINS) };

    const app = new Vue({
        data: {
            dispenseErrorCount: 0,
            taskCount: { individual: 0, team: 0, open: 0, overdue: 0, completedToday: 0 },
            notificationCount: 0,
            dashboardData: new DashboardStatus(),
            dashboardDataUpdated: updateTime,
            phoneStats: new PhoneStats(),
            environment: "",
            version: "",
            dataHubFileVersion: {},
            dataHubPhoneStats: {
                longestHold: null,
                callsHolding: null,
                repsReady: null
            },
            dataHubState: "",
            phoneStatsEnabled: null as boolean | null, // null = unknown, true = enabled, false = disabled
            user: new User(),
            permissions: [],
            remoteDisabledStates: [] as string[],
            stateRestrictedStates: [] as string[],
            pageNames: {},
            userSettings: {},
            // False until the bulk UserSettings payload lands over SignalR; an empty userSettings
            // alone cannot tell "not delivered yet" from "delivered, nothing saved".
            userSettingsLoaded: false,
            // The tenant configuration values the shell reads, fetched once per session by
            // tenantConfigService -- only the fields it names, never the whole row.
            // Same shape and same reason for the companion flag as userSettings above: an empty
            // object cannot tell "not fetched yet" from "nothing configured", and a feature must
            // never switch on before the tenant's toggles are known.
            tenantConfig: {} as Record<string, any>,
            tenantConfigLoaded: false,
            // True while a guided tour run is active. The footer's `?` listener and the top nav's
            // F-key listeners return early while it is set, so a stray hotkey cannot open a modal
            // over the tour overlay.
            tourActive: false,
            textShortcodesVersion: 0,
            inactivityState: idleInactivityState(),
        },
        async created() {
            if (this.$authProviderType === "Cognito") {
                console.log("startedBeforeCreate");
                const connectionsOptions = {
                    accessTokenFactory: async () => {
                        const resp = await getCognitoToken();
                        return resp;
                    },
                } as signalR.IHttpConnectionOptions;
                dashboardSetup(this, serverURL, connectionsOptions);
                inactivitySetup(this, inactivityOptions);
                presenceSetup(this);
            } else {
                // Read from the URL: the first navigation may not have resolved yet, so $route can
                // still be the router's start location.
                const isHomeRoute = router.match(window.location.pathname).name === "Home";
                let isAuthenticated = this.$msal.isAuthenticated();

                if (!isAuthenticated && !isHomeRoute) {
                    try {
                        await MicrosoftAuth.ensureAuthenticated();
                        isAuthenticated = this.$msal.isAuthenticated();
                    } catch (e) {
                        if (e instanceof BrowserAuthError) {
                            await this.$router.push({ name: "Home" });
                        }
                    }
                }

                if (!isAuthenticated) return;

                const connectionsOptions = {
                    accessTokenFactory: async () => {
                        const token = await MicrosoftAuth.fetchToken();
                        return token.accessToken;
                    },
                } as signalR.IHttpConnectionOptions;
                dashboardSetup(this, serverURL, connectionsOptions);
                inactivitySetup(this, inactivityOptions);
                presenceSetup(this);
            }
        },
        render: h => h(App),
        router,
    });

    Object.defineProperty(Vue.prototype, '$user', {
        get() {
            return app.user;
        },
        set(value) {
            app.user = value ?? new User();
        },
    });

    Object.defineProperty(Vue.prototype, '$permissions', {
        get() {
            return app.permissions;
        },
        set(value) {
            app.permissions = value ?? [];
        },
    });

    Object.defineProperty(Vue.prototype, '$remoteDisabledStates', {
        get() {
            return app.remoteDisabledStates;
        },
        set(value) {
            app.remoteDisabledStates = value ?? [];
        },
    });

    Object.defineProperty(Vue.prototype, '$stateRestrictedStates', {
        get() {
            return app.stateRestrictedStates;
        },
        set(value) {
            app.stateRestrictedStates = value ?? [];
        },
    });

    Object.defineProperty(Vue.prototype, '$pageNames', {
        get() {
            return app.pageNames;
        },
        set(value) {
            app.pageNames = value ?? {};
        },
    });

    initUserSettings(app);
    initTenantConfig(app);
    initUpdateCheck(app);

    app.$mount("#app");

    // The splash covers the first navigation, guards included, so nothing shows between it and the
    // first page. A failed first navigation still drops it, leaving whatever rendered visible.
    router.onReady(() => {
        hideSplash();
        prefetchCommonPages();
    }, hideSplash);
}

// Start the application
initializeApp().catch(error => {
    console.error('Failed to initialize application:', error);
    hideSplash();
});
